import React, { useState, useEffect } from "react";
import { href, Link, useLocation, useNavigate } from "react-router-dom";
import { cn } from "../lib/utils";
import { getCompany } from "../api/officeandcompany";
import { getMyEmployeeProfile } from "../api/employees";
import { getMyAssignedLioIssues } from "../api/leoIssues";
import {
  HomeIcon,
  UsersIcon,
  ClockIcon,
  CalendarIcon,
  DocumentTextIcon,
  ChartBarIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  BriefcaseIcon,
  CurrencyRupeeIcon,
  BanknotesIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  InboxIcon,
  UserMinusIcon,
  ComputerDesktopIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../contexts/AuthContext";
import { useAccess } from "../contexts/AccessContext";
import { panelKeyForPath, panelPathIsActive } from "../config/panelCatalog";
import { useWorkflowTrial } from "../limited-workspace/useWorkflowTrial";
import { useSharedMeetlyCount } from "../hooks/useSharedMeetlyCount";
import { MEETLY_SHARED_PATH, meetlyNavHref } from "../utils/meetlyShare";
import {
  ClipboardList,
  MessagesSquare,
  QrCode,
  ListTodo,
  CheckSquare,
  UsersRound,
  DownloadIcon,
  CalendarDays,
} from "lucide-react";

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  isMinimized: boolean;
  setIsMinimized: (val: boolean) => void;
}

export function Sidebar({
  isOpen,
  onClose,
  isMinimized,
  setIsMinimized,
}: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const workflowTrial = useWorkflowTrial();
  const { hasAccess, isPathVisible } = useAccess();
  // Meeting for a colleague without Meetly: only there to reach "Shared with me".
  const meetlyVisible = !hasAccess || isPathVisible("/meetly");
  const meetlyHref = meetlyNavHref(meetlyVisible, useSharedMeetlyCount(!meetlyVisible));
  const [expandedSections, setExpandedSections] = useState<string[]>([]);
  const [companyName, setCompanyName] = useState<string>("");
  const [fullName, setFullName] = useState<string>("");
  // Whether this person has any Lio question assigned to them. Only a handful
  // of people in one workspace ever do, so the entry is hidden for everyone
  // else rather than sitting in 400 sidebars doing nothing. The bell is the
  // other way in; this is what makes the page findable again afterwards.
  const [hasLioIssues, setHasLioIssues] = useState(false);

  useEffect(() => {
    let active = true;
    getMyAssignedLioIssues()
      .then((rows) => {
        if (active) setHasLioIssues(rows.length > 0);
      })
      .catch(() => {
        /* never block the sidebar on it — the entry just stays hidden */
      });
    return () => {
      active = false;
    };
  }, [user]);

  // Company name comes from GET /getCompanyDetail (see api/officeandcompany.ts).
  useEffect(() => {
    let active = true;
    getCompany()
      .then((res) => {
        const company = res?.companies?.[0];
        if (active && company?.companyName) setCompanyName(company.companyName);
      })
      .catch((err) =>
        console.error("Sidebar: failed to load company name", err)
      );
    return () => {
      active = false;
    };
  }, [user]);

  // Full name (first + last) for the header. The JWT only carries the first
  // name (user.name), so resolve the caller's own employee row for the full
  // name. Some users (e.g. a company-owner admin) have no employee row — that
  // 404s, so we silently keep the first-name fallback.
  useEffect(() => {
    let active = true;
    getMyEmployeeProfile()
      .then((res) => {
        const full = (
          res?.name || `${res?.first_name || ""} ${res?.last_name || ""}`
        ).trim();
        if (active && full) setFullName(full);
      })
      .catch(() => {
        /* no employee row for this user → fall back to user.name */
      });
    return () => {
      active = false;
    };
  }, [user]);

  // Roles are stored lower-case (e.g. "hr head"); show them title-cased.
  const formatRole = (role?: string) =>
    (role || "")
      .split(" ")
      .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
      .join(" ");

  const toggleSection = (section: string) => {
    // if (isMinimized) return;
    if (isMinimized) {
      setIsMinimized(false);
    }
    setExpandedSections((prev) =>
      prev.includes(section)
        ? prev.filter((s) => s !== section)
        : [...prev, section]
    );
  };

  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
    setExpandedSections([]);
  };

  // Which Payroll submenu to show: the org-wide Records/Approval set, or the
  // self-service Payroll/Tax Summary set. Access Management decides — granting
  // Payroll Records or Payroll Approval to ANY role switches them to the admin
  // set. Only a user with no access profile falls back to role === "admin".
  const payrollAdminView = hasAccess
    ? isPathVisible("/payroll-record") || isPathVisible("/approval-settings")
    : user?.role === "admin";
  // The personal payslip / tax-declaration entries. Non-catalog routes, so an
  // access profile can't hide them — with a profile everyone keeps them; without
  // one we preserve the legacy behaviour where admin had no self-service payroll.
  const payrollSelfView = hasAccess ? true : user?.role !== "admin";

  const menuItems = [
    {
      name: "Dashboard",
      href: "/dashboard",
      icon: HomeIcon,
      roles: ["founder", "director", "admin", "hr head", "hr admin", "manager", "supervisor", "employee"],
    },
    {
      name: "My Tasks",
      href: "/client-portal",
      icon: ClipboardList,
      // Same portal page serves both client-linked and vendor-linked users.
      // Backend resolves access by ClientDetails.linked_user_id regardless
      // of entity_type, so both roles see the same data scope.
      roles: ["client", "vendor"],
    },
    // {
    //   name: "Messages",
    //   href: "/chat",
    //   icon: MessageSquare,
    //   roles: ["founder", "director", "admin", "hr head", "manager", "supervisor", "employee"],
    //   badge: "chat-unread" as const,
    // },
    // {
    //   name: "Calendar",
    //   href: "/calendar",
    //   icon: CalendarDays,
    //   roles: ["founder", "director", "admin", "hr head", "manager", "supervisor", "employee"],
    // },
    {
      name: "Employees",
      href: "/employees",
      icon: UsersIcon,
      roles: ["founder", "director", "admin", "hr head", "hr admin", "manager", "supervisor"],
    },
    {
      name: "Attendance",
      href: "/attendance",
      icon: ClockIcon,
      roles: ["founder", "director", "admin", "hr head", "hr admin", "manager", "supervisor", "employee"],
    },
    {
      name: "Leaves",
      href: "/leaves",
      icon: CalendarIcon,
      roles: ["founder", "director", "admin", "hr head", "hr admin", "manager", "supervisor", "employee"],
    },
    {
      name: "Expenses",
      href: "/expenses",
      icon: BanknotesIcon,
      roles: ["founder", "director", "admin", "hr head", "hr admin", "manager", "supervisor", "employee"],
    },
    // {
    //   name: "Allowances",
    //   href: "/allowances",
    //   icon: BanknotesIcon,
    //   roles: ["founder", "director", "admin", "hr head", "hr admin", "manager", "supervisor", "employee"],
    // },
    // {
    //   name: "Penalties",
    //   href: "/penalties",
    //   icon: BanknotesIcon,
    //   roles: ["founder", "director", "admin", "hr head", "hr admin", "manager", "supervisor", "employee"],
    // },
    {
      name: "Assets",
      href: "/assets",
      icon: ComputerDesktopIcon,
      roles: ["founder", "director", "admin", "hr head", "hr admin", "manager", "supervisor", "employee"],
    },
    {
      name: "Documents",
      href: "/documents",
      icon: DocumentTextIcon,
      roles: ["founder", "director","hr head", "hr admin", "manager", "supervisor", "employee"],
    },
    {
      name: "Task Management",
      icon: ClipboardList,
      roles: ["founder", "director", "admin", "hr head", "hr admin", "manager", "supervisor", "employee"],
      children: [
        { name: "Daily Planner", href: "/todo" },
        { name: "Notemaker", href: "/notes" },
        { name: "Meeting", href: "/meetly" },
        { name: "Projects", href: "/projects" },
        { name: "Tasks", href: "/tasks/list" },
        { name: "Clients", href: "/clients" },
        { name: "Vendors", href: "/vendors" },
        { name: "Reports", href: "/tasks/reports" },
      ],
    },

    {
      name: "Work Flow",
      icon: BriefcaseIcon,
      // Section opened up to every role so a regular employee can reach their
      // OWN Activity Reports (the page self-scopes to the signed-in user).
      // The manager-only children below stay gated by their own `roles`.
      roles: ["founder", "director", "admin", "hr head", "hr admin", "manager", "supervisor", "employee", "intern"],
      children: [
        { name: "Work Schedule", href: "/screen-recording", roles: ["founder", "director", "admin", "hr head", "hr admin", "manager"] },
        // No `roles` → visible to everyone; employees see only their own activity.
        { name: "Activity Reports", href: "/activity-reports" },
        { name: "Activity Comparison", href: "/activity-comparison", roles: ["founder", "director", "admin", "hr head", "hr admin", "manager"] },
      ],
    },
    // One Payroll group. The org-wide Records/Approval entries appear for
    // whoever Access Management grants them to; the personal salary & tax
    // declaration entries stay for everyone who has them, so being granted
    // Payroll Records never costs a user their OWN payslip / tax summary.
    {
      name: "Payroll",
      icon: CurrencyRupeeIcon,
      roles: ["founder", "director", "admin", "hr head", "hr admin", "manager", "supervisor", "employee"],
      children: [
        ...(payrollAdminView
          ? [
              { name: "Payroll Records", href: "/payroll-record" },
              { name: "Payroll Approval", href: "/approval-settings" },
            ]
          : []),
        ...(payrollSelfView
          ? [
              { name: "Payroll", href: "/my-payroll" },
              { name: "Tax Summary", href: "/salary-tax-declaration" },
            ]
          : []),
        // Same route in both sets — listed once so it never duplicates.
        { name: payrollAdminView ? "Reports" : "My Reports", href: "/payroll/reports" },
      ],
    },
    {
      // Every role: the page is self-service for anyone inviting a guest. The
      // front-desk side (scanner, branch register) is gated inside the page by
      // the "visitors" panel at Manage, not by this list.
      name: "Visitor Management",
      href: "/visitors",
      icon: QrCode,
      roles: ["founder", "director", "admin", "hr head", "hr admin", "manager", "supervisor", "employee"],
    },
    {
      name: "Feedback & Complaints",
      href: "/grievances",
      icon: MessagesSquare,
      roles: ["founder", "director", "admin", "hr head", "hr admin", "manager", "supervisor", "employee"],
    },
    // {
    //   name: "Job Postings",
    //   href: "/jobs",
    //   icon: BriefcaseIcon,
    //   roles: ["founder","director","admin", "hr head", "manager", "supervisor"],
    // },
    // {
    //   name: "Reports",
    //   href: "/reports",
    //   icon: ChartBarIcon,
    //   roles: ["founder","director","admin", "hr head", "manager", "supervisor"],
    // },
    {
      name: "Downloads",
      href: "/downloads",
      icon: DownloadIcon,
      roles: ["founder", "director", "admin", "hr head", "hr admin", "manager", "supervisor", "employee"],
    },

    {
      name: "Separation Management",
      href: "/separation/dashboard",
      icon: UserMinusIcon,
      roles: ["founder", "director", "admin", "hr head", "hr admin"],
      // children: [
      //   { name: "Dashboard", href: "/separation/dashboard" },
        // { name: "Resignations", href: "/separation/resignations" },
        // { name: "Terminations", href: "/separation/terminations" },
        // { name: "Exit Checklists", href: "/separation/checklists" },
        // { name: "Settlements", href: "/separation/settlements" },
      // ],
    },
    // Lio questions handed to this person by the superadmin. No role list and
    // no panel: the endpoint is self-only, so "do they have any" IS the gate.
    ...(hasLioIssues
      ? [
          {
            name: "My Lio Questions",
            href: "/my-lio-issues",
            icon: InboxIcon,
            roles: [] as string[],
          },
        ]
      : []),
    // Employee Resignation - Only for employees
    {
      name: "My Resignation",
      href: "/my-resignation",
      icon: UserMinusIcon,
      roles: ["employee"],
    },
    // {
    //   name: "Chat",
    //   href: "/chat",
    //   icon: MessageSquare,
    //   roles: ["founder","director","admin", "hr head", "manager", "supervisor", "employee"],
    // },
  ];

  // Intern is the employee tier — treat it as "employee" for every role-gated
  // nav item so interns see exactly what employees see (My Resignation, etc.).
  const _role = (user?.role || "admin");
  const _tierRole = _role.toLowerCase() === "intern" ? "employee" : _role;
  // An EMPTY roles list means "no role restriction", not "nobody" — the item
  // carries its own gate instead (My Lio Questions renders only when the
  // person actually has one assigned). `[].includes(x)` is false, so without
  // this such an item silently never appears, which is exactly what happened.
  const roleOk = (item: any) =>
    !item.roles || item.roles.length === 0 || item.roles.includes(_tierRole);

  // Sidebar visibility:
  //  • No access profile assigned → pure role-based (original behaviour).
  //  • Access profile assigned → the profile is AUTHORITATIVE for catalog
  //    panels: show exactly the panels it enables, ignoring the base role (an
  //    access can GRANT panels the role alone wouldn't, e.g. Payroll for an
  //    employee). Non-catalog items (My Resignation, My Tasks) aren't covered
  //    by access, so they stay role-based.
  const roleAndAccessMenuItems = menuItems
    .map((item: any) => {
      if (!hasAccess) {
        return roleOk(item) ? item : null;
      }
      if (!item.children) {
        const href = item.href || "";
        const gated = panelKeyForPath(href) != null;
        if (gated) return isPathVisible(href) ? item : null;
        return roleOk(item) ? item : null; // non-catalog personal item
      }
      // Parent group — children are catalog panels gated by the profile.
      const kids = item.children
        .map((c: any) => (c.href === "/meetly" ? (meetlyHref ? { ...c, href: meetlyHref } : null) : c))
        .filter((c: any) => c && (c.href === MEETLY_SHARED_PATH || isPathVisible(c.href)));
      return kids.length ? { ...item, children: kids } : null;
    })
    .filter(Boolean) as typeof menuItems;

  // Free-plan users keep the normal Teamlio sidebar presentation. Only the
  // available destinations change; no separate visual shell is introduced.
  const visibleMenuItems =
    user?.subscription?.workspace_mode === "limited"
      ? [
          isPathVisible("/dashboard") && {
            ...menuItems[0],
            href: "/limited-workspace/dashboard",
          },
          isPathVisible("/employees") && {
            ...menuItems.find((item: any) => item.name === "Employees"),
            href: "/limited-workspace/employees",
          },
          isPathVisible("/attendance") && {
            ...menuItems.find((item: any) => item.name === "Attendance"),
            href: "/limited-workspace/attendance",
          },
          (isPathVisible("/todo") || isPathVisible("/tasks")) && {
            ...menuItems.find((item: any) => item.name === "Task Management"),
            children: [
              isPathVisible("/todo")
                ? { name: "Daily Planner", href: "/limited-workspace/planner" }
                : null,
              isPathVisible("/tasks")
                ? { name: "Tasks", href: "/limited-workspace/tasks" }
                : null,
            ].filter(Boolean),
          },
          isPathVisible("/leaves") && {
            ...menuItems.find((item: any) => item.name === "Leaves"),
            href: "/limited-workspace/leave",
          },
          workflowTrial.visible &&
            (() => {
              const workFlow = menuItems.find(
                (item: any) => item.name === "Work Flow"
              );
              const children = workFlow?.children?.filter((child: any) =>
                isPathVisible(child.href)
              );
              return children?.length ? { ...workFlow, children } : null;
            })(),
          isPathVisible("/downloads") &&
            menuItems.find((item: any) => item.name === "Downloads"),
        ].filter((item: any) => item?.name)
      : roleAndAccessMenuItems;

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 md:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar — fixed/off-canvas on mobile, in-flow on desktop so only one
          aside ever paints regardless of which wrapper rendered it. */}
      <aside
        className={cn(
          "gt-sidebar transition-all duration-300",
          isMinimized && "is-minimized",
          isMinimized ? "w-20" : "w-64",
          "fixed top-0 left-0 z-50 md:static md:translate-x-0",
          isOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        )}
      >
        {/* Header */}
        <div
          className={cn(
            "gt-sidebar-header",
            isMinimized ? "justify-center" : "justify-between"
          )}
        >
          {!isMinimized && (
            <div className="gt-sidebar-identity">
              <span
                className="gt-sidebar-user"
                title={fullName || user?.name || "User"}
              >
                {fullName || user?.name || "User"}
              </span>
              {user?.role && (
                <span className="gt-sidebar-role">
                  ({formatRole(user.role)})
                </span>
              )}
              {companyName && (
                <span className="gt-sidebar-company">{companyName}</span>
              )}
            </div>
          )}

          <button
            onClick={toggleMinimize}
            className="gt-sidebar-collapse hidden md:inline-flex"
            aria-label={isMinimized ? "Expand sidebar" : "Collapse sidebar"}
          >
            {isMinimized ? (
              <ChevronDoubleRightIcon className="h-4 w-4" />
            ) : (
              <ChevronDoubleLeftIcon className="h-4 w-4" />
            )}
          </button>

          <button
            className="gt-sidebar-collapse md:hidden ml-auto"
            onClick={onClose}
            aria-label="Close menu"
          >
            ✖
          </button>
        </div>

        {/* Menu */}
        <nav className="gt-sidebar-nav">
          {visibleMenuItems.map((item) => {
            const Icon = item.icon;
            const hasChildren = !!item.children;
            const showWorkflowTrialBadge =
              workflowTrial.limited && item.name === "Work Flow";

            const isActive = item.children
              ? item.children.some((c) =>
                location.pathname.startsWith(c.href)
              )
              : location.pathname.startsWith(item.href || "");

            const isExpanded =
              !(showWorkflowTrialBadge && workflowTrial.expired) &&
              (expandedSections.includes(item.name) || isActive);

            return (
              <div key={item.name} className="gt-sidebar-item-wrap relative">
                {hasChildren ? (
                  <>
                    <button
                      onClick={() =>
                        showWorkflowTrialBadge && workflowTrial.expired
                          ? navigate("/downloads")
                          : toggleSection(item.name)
                      }
                      className={cn(
                        "gt-sidebar-item",
                        isActive && "is-active",
                        showWorkflowTrialBadge &&
                          workflowTrial.expired &&
                          "opacity-60"
                      )}
                      aria-expanded={isExpanded}
                      title={
                        showWorkflowTrialBadge && workflowTrial.expired
                          ? "1 month trial period is over. Open Downloads to renew."
                          : isMinimized
                            ? item.name
                            : ""
                      }
                    >
                      <Icon className="gt-sidebar-icon" />
                      {!isMinimized && (
                        <>
                          <span className="gt-sidebar-label">
                            {item.name}
                          </span>
                          {showWorkflowTrialBadge && (
                            <span className="ml-auto inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[9px] font-semibold leading-none text-emerald-700">
                              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                              {workflowTrial.expired
                                ? "Trial over"
                                : "1 month free trial"}
                            </span>
                          )}
                          {isExpanded ? (
                            <ChevronDownIcon className="gt-sidebar-chevron" />
                          ) : (
                            <ChevronRightIcon className="gt-sidebar-chevron" />
                          )}
                        </>
                      )}
                    </button>

                    {!isMinimized && isExpanded && (
                      <div className="gt-sidebar-submenu">
                        {item.children
                          // A child's `roles` list is only the no-profile
                          // fallback. With an access profile assigned the
                          // profile is authoritative (children were already
                          // filtered by isPathVisible above), so the hardcoded
                          // list must not hide a panel Access Management granted.
                          .filter(
                            (child) =>
                              hasAccess ||
                              !(child as any).roles ||
                              (child as any).roles.includes(_tierRole),
                          )
                          .map((child) => (
                            <Link
                              key={child.name}
                              to={child.href}
                              onClick={onClose}
                              className={cn(
                                "gt-sidebar-subitem",
                                panelPathIsActive(location.pathname, child.href) && "is-active"
                              )}
                            >
                              {child.name}
                            </Link>
                          ))}
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to={item.href!}
                    onClick={onClose}
                    className={cn(
                      "gt-sidebar-item",
                      isActive && "is-active"
                    )}
                    title={isMinimized ? item.name : ""}
                  >
                    <Icon className="gt-sidebar-icon" />
                    {!isMinimized && (
                      <span className="gt-sidebar-label">{item.name}</span>
                    )}
                  </Link>
                )}

                {/* Tooltip on collapsed sidebar */}
                {isMinimized && (
                  <span className="gt-sidebar-tooltip">{item.name}</span>
                )}
              </div>
            );
          })}
        </nav>
      </aside>
    </>
  );
}
